import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Download } from 'lucide-react';
import { useAuthStore } from '@auth/authStore';
import { getComponents } from '@entities';
import { useAsyncResource, useUrlAssetScope, useRegisterFilters, useScopeOptions } from '@hooks';
import { FilterBar, TablePagination } from '@components';
import { downloadCSV } from '@shared/utils/exportCSV';
import {
  componentKey, componentFilters, componentSearchText, inScope, textValue,
  sortComponents, componentExportRows, sameComponentCode,
} from '@/entities/component/component.model';
import { rdsColumns, componentTableColumns } from './component.columns';
import ComponentDetail from './components/ComponentDetail';
import RegisterTable from '@/shared/components/RegisterTable/RegisterTable';
import styles from './ComponentRegister.module.css';

function RegisterContent({ companyId, siteId, assetId }) {
  const [params, setParams] = useSearchParams();
  const locations = useScopeOptions();
  const resource = useAsyncResource(async () => {
    const rows = await getComponents({ companyId, siteId, assetId });
    if (!Array.isArray(rows)) throw new Error('The component API must return a list.');
    return rows.filter(row => inScope(row, siteId, assetId));
  }, [companyId, siteId, assetId], { initialData: [] });

  const rows = useMemo(() => {
    const sites = new Map(locations.sites.map(site => [site.id, site.label]));
    const assets = new Map(locations.assets.map(asset => [asset.key, asset.label]));
    return resource.data.map(raw => ({
      ...raw,
      raw,
      siteLabel: sites.get(textValue(raw.site_id)) || textValue(raw.site_id) || 'Location not recorded',
      assetLabel: assets.get(JSON.stringify([textValue(raw.site_id), textValue(raw.asset_id)])) ||
        textValue(raw.asset_id) || 'Asset not recorded',
    }));
  }, [resource.data, locations.sites, locations.assets]);

  const filters = useRegisterFilters({
    namespace: 'components', items: rows,
    definitions: componentFilters, getSearchText: componentSearchText,
  });
  const [sort, setSort] = useState({ key: 'rds_code', direction: 'asc' });
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ key: '', page: 0 });
  const sorted = useMemo(() => sortComponents(filters.filtered, sort, componentTableColumns),
    [filters.filtered, sort]);
  const pageKey = JSON.stringify([filters.query, filters.values, sort, rowsPerPage]);
  const lastPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
  const page = pagination.key === pageKey ? Math.min(pagination.page, lastPage) : 0;
  const visible = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const selectedKey = params.get('components.selected');
  const matches = selectedKey ? rows.filter(row => componentKey(row) === selectedKey) : [];
  const selected = matches.length === 1 ? matches[0] : null;
  const canMatchAnomalies = selected &&
    rows.filter(row => sameComponentCode(row, selected)).length === 1;

  function select(key) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (key) next.set('components.selected', key);
      else next.delete('components.selected');
      return next;
    });
  }



  if (resource.isLoading) return <div className={styles.feedback} role="status">Loading inspection objects…</div>;
  if (resource.error) return (
    <div className={styles.feedback}>
      <p role="alert">{resource.error}</p>
      <button type="button" className={styles.button} onClick={() => resource.reload()}>Retry loading objects</button>
    </div>
  );

  return (
    <>
      <FilterBar filters={filters} noun="components" actions={
        <button type="button" className={styles.button} disabled={!sorted.length} onClick={() => {
          downloadCSV(componentExportRows(sorted, rdsColumns),
            `${siteId || 'allSites'}-${assetId || 'allAssets'}-components.csv`);
        }}>
          <Download size={16} aria-hidden="true" /> Export CSV
        </button>
      } />

      {locations.error && <p className={styles.notice}>
        Location names are unavailable; IDs are shown.
        {' '}<button type="button" className={styles.link} onClick={() => locations.reload()}>Retry names</button>
      </p>}

      {selectedKey && !selected && <div className={styles.notice} role="status">
        <p>{matches.length > 1
          ? 'This component identity matches multiple records. The catalogue needs a unique record ID.'
          : 'The selected component is not available in this location scope.'}</p>
        <button type="button" className={styles.link} onClick={() => select(null)}>Dismiss selection</button>
      </div>}

      <div className={styles.tableArea}>
        {sorted.length ? (
          <RegisterTable
            rows={visible} columns={componentTableColumns} sort={sort} onSortChange={setSort}
            getRowKey={componentKey} selectedKey={selectedKey}
            onOpen={row => select(componentKey(row))} caption="Registered inspection objects"
            renderCell={(row, column) => {
              if (column.key === 'rds_code') return <>
                {textValue(row.rds_code) || 'Component without RDS code'}
                <small>{textValue(row.description) || 'No description recorded'}</small>
              </>;
              if (column.key === 'location') return <>{row.assetLabel}<small>{row.siteLabel}</small></>;
              if (column.key === 'subsystem') return <span className={styles.groupLabel}>{column.value(row)}</span>;
              if (column.key === 'technique') return <span className={styles.techniques}>{column.value(row)}</span>;
              return column.value(row);
            }}
          />
        ) : <div className={styles.feedback}>
          <p>{rows.length ? 'No components match these filters.' : 'No inspection objects are registered in this scope.'}</p>
          {rows.length > 0 && <button type="button" className={styles.button} onClick={filters.clear}>Clear search and filters</button>}
        </div>}
      </div>

      <TablePagination count={sorted.length} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_event, nextPage) => setPagination({ key: pageKey, page: nextPage })}
        onRowsPerPageChange={event => setRowsPerPage(Number(event.target.value))}
        rowsPerPageOptions={[25, 50, 100]} />

      {selected && <ComponentDetail key={componentKey(selected)} component={selected}
        companyId={companyId} canMatchAnomalies={canMatchAnomalies}
        onClose={() => select(null)} />}
    </>
  );
}

export default function ComponentRegister() {
  const companyId = useAuthStore(state => state.companyID);
  const { siteId, assetId } = useUrlAssetScope();
  return (
    <section className={styles.page}>
      <header className={styles.heading}>
        <span>INSPECTION OBJECT DATABASE</span>
        <h1>The component, in context.</h1>
        <p>Explore the RDS-PP catalogue and its evidence. Technique filters show eligibility, not assigned or completed inspections.</p>
      </header>
      {companyId ? <RegisterContent key={JSON.stringify([companyId, siteId, assetId])}
        companyId={companyId} siteId={siteId} assetId={assetId} /> :
        <p className={styles.feedback}>Select a company to view inspection objects.</p>}
    </section>
  );
}